import { useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';

import { AnalyticsEventNames } from '~/constants/AnalyticsEventMap';
import { useTracking } from '~/hooks/tracking/useTracking';
import { useWindowSize } from '~/hooks/useWindowSize';
import type { CMSBadge } from '~/networking/api/cms/responses/CMSWineVintage';

interface BadgesProps {
    badges: CMSBadge[];
}

export function Badges({ badges }: BadgesProps): JSX.Element {
    const [showAll, setShowAll] = useState(false);
    const { isDesktop } = useWindowSize();
    const { captureEvent } = useTracking();
    const showAllBadges = () => {
        if (showAll) {
            setShowAll(false);
            return;
        }
        captureEvent(AnalyticsEventNames.BadgeLoadMore);
        setShowAll(true);
    };

    const handleBadgeTracking = () => {
        captureEvent(AnalyticsEventNames.SelectBadge);
    };

    return (
        <BadgeSection>
            <BadgesList className={`${showAll ? 'showingAll' : ''}`}>
                {badges?.map(({ name, description, statistic, statisticDescription, image }) => {
                    return (
                        <BadgeItem
                            key={name}
                            href={'https://www.vinovest.co/blog/vinovest-badges-the-official-guide'}
                            onClick={handleBadgeTracking}
                            target={'_blank'}
                        >
                            <Sticker>
                                <Image alt={'vinovest badge'} src={image.url} width={50} height={50} />{' '}
                            </Sticker>
                            <Name>{name}</Name>
                            <Description>{description}</Description>
                            <Statistic>
                                <Percentage>{statistic}%</Percentage> of {statisticDescription}{' '}
                            </Statistic>
                        </BadgeItem>
                    );
                })}
            </BadgesList>
            {badges.length > 3 && isDesktop && (
                <LoadButton onClick={showAllBadges}>{showAll ? 'Hide' : 'Load more'}</LoadButton>
            )}
        </BadgeSection>
    );
}

const BadgeSection = styled.section`
    margin: 0 auto 57px;
    max-width: 936px;
`;

const BadgesList = styled.div`
    align-items: center;
    display: flex;
    gap: 28px;
    height: 158px;
    overflow-x: scroll;
    overflow-y: hidden;
    padding-top: 32px;
    position: relative;
    &:after {
        background: rgb(255, 255, 255);
        background: linear-gradient(
            0deg,
            rgba(255, 255, 255, 1) 0%,
            rgba(255, 255, 255, 1) 75%,
            rgba(255, 255, 255, 0) 100%
        );
        content: ' ';
        display: block;
        height: calc(100% - 158px);
        opacity: 1;
        position: absolute;
        top: 158px;
        transition: all 600ms ease-in-out;
        width: 100%;
        z-index: 99;
    }
    &.showingAll {
        height: auto;
        &:after {
            opacity: 0;
            top: 100%;
        }
    }
    @media screen and (min-width: 1024px) {
        flex-wrap: wrap;
        overflow: hidden;
        padding-top: 20px;
    }
`;

const BadgeItem = styled.a`
    border: 1px solid #caccce;
    border-radius: 6px;
    display: flex;
    flex-flow: column;
    height: 133px;
    margin: 0 0 28px 0;
    min-width: 288px;
    width: 288px;
    padding: 14px 24px 39px;
    position: relative;
`;

const Sticker = styled.span`
    position: absolute;
    right: -15px;
    top: -15px;
`;

const Name = styled.span`
    font-family: var(--font-roslindale);
    font-size: 18px;
    font-weight: 400;
`;

const Description = styled.p`
    color: #767a7f;
    font-size: 11px;
    margin: 6px 0 0;
`;

const Statistic = styled.span`
    bottom: 10px;
    color: #606060;
    font-size: 12px;
    left: 24px;
    position: absolute;
`;

const Percentage = styled.span`
    background-color: #fae8d1;
    border-radius: 33px;
    color: #242e35;
    display: inline-flex;
    font-size: 14px;
    margin-right: 12px;
    padding: 2px 6px;
`;

const LoadButton = styled.button`
    align-items: center;
    border: 1px solid #caccce;
    border-radius: 6px;
    display: flex;
    font-size: 11px;
    height: 36px;
    justify-content: center;
    margin-top: 20px;
    text-align: center;
    text-transform: uppercase;
    width: 100%;
`;
